import { useState } from 'react';
import { Database, FileJson, BarChart3, Layers, Eye, Plus, X, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import useFlowStore from '../stores/useFlowStore';
import { createNode, generateId } from '../utils/helpers';
import { MODEL_TYPES, NODE_TYPES } from '../utils/constants';

const entityTypes = [
  { 
    type: NODE_TYPES.TABLE, 
    label: 'Table', 
    icon: Database, 
    color: '#3b82f6',
    modelTypes: [MODEL_TYPES.RELATIONAL]
  },
  { 
    type: NODE_TYPES.VIEW, 
    label: 'View', 
    icon: Eye, 
    color: '#06b6d4',
    modelTypes: [MODEL_TYPES.RELATIONAL, MODEL_TYPES.ANALYTICAL]
  },
  { 
    type: NODE_TYPES.COLLECTION, 
    label: 'Collection', 
    icon: FileJson, 
    color: '#10b981',
    modelTypes: [MODEL_TYPES.NOSQL]
  },
  { 
    type: NODE_TYPES.FACT, 
    label: 'Fact Table', 
    icon: BarChart3, 
    color: '#f59e0b',
    modelTypes: [MODEL_TYPES.ANALYTICAL]
  },
  { 
    type: NODE_TYPES.DIMENSION, 
    label: 'Dimension', 
    icon: Layers, 
    color: '#8b5cf6',
    modelTypes: [MODEL_TYPES.ANALYTICAL]
  },
  { 
    type: NODE_TYPES.TEXT, 
    label: 'Text Note', 
    icon: FileText, 
    color: '#fef9c3',
    modelTypes: [MODEL_TYPES.RELATIONAL, MODEL_TYPES.NOSQL, MODEL_TYPES.ANALYTICAL]
  },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { modelType, modelName, addNode, nodes } = useFlowStore();
  const [draggedType, setDraggedType] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Check if a model is active
  const hasActiveModel = modelName && modelName.trim() !== '';

  // Filter entity types based on current model type
  const availableTypes = entityTypes.filter(entity => {
    return entity.modelTypes.includes(modelType);
  });

  const onDragStart = (event, nodeType) => {
    if (!hasActiveModel) {
      event.preventDefault();
      return;
    }
    setDraggedType(nodeType);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/reactflow', nodeType);
  };

  const onDragEnd = () => {
    setDraggedType(null);
  };

  const handleQuickAdd = (nodeType) => {
    if (!hasActiveModel) {
      return;
    }
    const position = {
      x: Math.random() * 300 + 100,
      y: Math.random() * 300 + 100,
    };
    const newNode = createNode(nodeType, position, 'New Entity', nodes);
    addNode(newNode);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className={`fixed lg:relative left-0 top-0 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl lg:shadow-none z-50 flex flex-col transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-80'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          {!isCollapsed && (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Entities
            </h2>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:block p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200 transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            <button
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Model Type Info */}
        {!isCollapsed && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <span className="font-semibold">Model Type:</span>{' '}
              <span className="capitalize">{modelType}</span>
            </p>
          </div>
        )}

        {/* No Model Warning */}
        {!hasActiveModel && !isCollapsed && (
          <div className="mx-4 mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium mb-1">
              ⚠️ No Model Active
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-400">
              Create a new model or open an existing one to start adding entities.
            </p>
          </div>
        )}

        {/* Entity Types */}
        <div className="flex-1 overflow-y-auto p-4">
          {isCollapsed ? (
            // Collapsed view - Icons only
            <div className="space-y-3">
              {availableTypes.map((entity) => {
                const Icon = entity.icon;
                return (
                  <div
                    key={entity.type}
                    className={`group relative p-3 rounded-lg border-2 border-dashed transition-all ${
                      !hasActiveModel
                        ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                        : draggedType === entity.type
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-move'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-move'
                    }`}
                    draggable={hasActiveModel}
                    onDragStart={(e) => onDragStart(e, entity.type)}
                    onDragEnd={onDragEnd}
                    title={hasActiveModel ? entity.label : 'Create or open a model first'}
                  >
                    <div className="flex items-center justify-center">
                      <Icon size={24} style={{ color: entity.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Expanded view - Full cards
            <>
              <div className="space-y-3">
                {availableTypes.map((entity) => {
                  const Icon = entity.icon;
                  return (
                    <div
                      key={entity.type}
                      className={`group relative p-4 rounded-lg border-2 border-dashed transition-all ${
                        !hasActiveModel
                          ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
                          : draggedType === entity.type
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-move'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-move'
                      }`}
                      draggable={hasActiveModel}
                      onDragStart={(e) => onDragStart(e, entity.type)}
                      onDragEnd={onDragEnd}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${entity.color}20` }}
                        >
                          <Icon size={24} style={{ color: entity.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {entity.label}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Drag to canvas
                          </p>
                        </div>
                        <button
                          onClick={() => handleQuickAdd(entity.type)}
                          disabled={!hasActiveModel}
                          className={`transition-opacity p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-gray-700 dark:text-gray-200 ${
                            !hasActiveModel
                              ? 'opacity-30 cursor-not-allowed'
                              : 'opacity-0 group-hover:opacity-100 hover:shadow-md'
                          }`}
                          title={hasActiveModel ? 'Quick add' : 'Create or open a model first'}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Instructions */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-2">
                  Quick Tips
                </h3>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Drag entities to the canvas</li>
                  <li>• Click the + icon for quick add</li>
                  <li>• Double-click entity to edit</li>
                  <li>• Connect entities by dragging from handles</li>
                  <li>• Use mouse wheel to zoom</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer with Build Timestamp & Environment Info */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 text-[11px] text-gray-500 dark:text-gray-400 flex items-center justify-between flex-shrink-0">
          {!isCollapsed ? (
            <>
              <div className="flex flex-col">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  Env: <span className="uppercase text-blue-600 dark:text-blue-400 font-mono">{import.meta.env.MODE === 'development' ? 'DEV' : 'PROD'}</span>
                </span>
                <span className="text-[10px] text-gray-400">
                  Build: {import.meta.env.VITE_BUILD_TIME || new Date().toISOString().slice(0, 10)}
                </span>
              </div>
              <span className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-mono text-[9px]">
                v1.0.0
              </span>
            </>
          ) : (
            <div
              className="mx-auto font-mono text-[9px] text-blue-600 dark:text-blue-400 font-bold uppercase cursor-help"
              title={`Env: ${import.meta.env.MODE} | Build: ${import.meta.env.VITE_BUILD_TIME || ''}`}
            >
              {import.meta.env.MODE === 'development' ? 'DEV' : 'PROD'}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
